import DOMPurify from "dompurify";

/**
 * Shared HTML sanitization and rendering utilities.
 * Centralizes DOMPurify config so allowed tags/attrs stay consistent.
 */

const ALLOWED_TAGS = [
  // Table elements
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "caption",
  "colgroup",
  "col",
  // Block elements
  "div",
  "p",
  "br",
  "hr",
  // Inline formatting
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "strike",
  "sub",
  "sup",
  // Lists
  "ul",
  "ol",
  "li",
  // Links
  "a",
  // Headers
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];

// `style` is intentionally excluded — arbitrary inline styles can enable
// CSS-based data exfiltration (e.g. background-image: url(...)).
// All visual styling is applied via MUI sx props on the container instead.
const ALLOWED_ATTR = [
  "class",
  "colspan",
  "rowspan",
  "scope",
  "headers",
  "href",
  "target",
  "rel",
];

/**
 * Check if text contains HTML elements that warrant rendering as HTML
 * rather than plain text / markdown.
 */
export function containsRenderableHtml(text: string): boolean {
  return /<(table|div|span|p|br|b|strong|i|em|u|ol|ul|li)[\s>]/i.test(text);
}

/**
 * Sanitize HTML for safe rendering via dangerouslySetInnerHTML.
 *
 * Adds rel="noopener noreferrer" to links that use target, and strips
 * all tags/attributes not in the allowlists above.
 */
export function sanitizeHtml(html: string): string {
  // Use a one-off DOMPurify instance to avoid global hook races when
  // multiple components sanitize during the same React render pass.
  const purify = DOMPurify();

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A" && node.getAttribute("target")) {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

/**
 * Check if sanitized HTML has meaningful visible content
 * (non-empty text or at least one table).
 */
export function hasMeaningfulContent(html: string): boolean {
  if (!html || !html.trim()) return false;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const textContent = doc.body?.textContent?.trim() || "";
  const hasTables = doc.querySelector("table") !== null;
  return textContent.length > 0 || hasTables;
}

/** MUI sx styles for rendering sanitized HTML content (tables, lists, etc.) */
export const htmlContentStyles = {
  fontSize: "14px",
  lineHeight: 1.6,
  color: "#424242",
  "& table": {
    borderCollapse: "collapse" as const,
    borderSpacing: 0,
    width: "100%",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    my: 1,
  },
  "& th, & td": {
    border: "1px solid #e0e0e0",
    padding: "8px 12px",
    textAlign: "left" as const,
    fontSize: "13px",
  },
  "& th": {
    backgroundColor: "#f5f5f5",
    fontWeight: 600,
    color: "#333",
  },
  "& td": {
    backgroundColor: "#ffffff",
  },
  "& tr:nth-of-type(even) td": {
    backgroundColor: "#fafafa",
  },
  "& tr:hover td": {
    backgroundColor: "#f0f7ff",
  },
  "& ul, & ol": {
    paddingLeft: "20px",
    my: 0.5,
  },
  "& a": {
    color: "#1976d2",
    textDecoration: "underline",
  },
  "& code": {
    backgroundColor: "#e0e0e0",
    padding: "2px 4px",
    borderRadius: "3px",
    fontSize: "13px",
  },
  "& pre": {
    backgroundColor: "#1e1e1e",
    padding: "12px",
    borderRadius: "4px",
    overflow: "auto",
  },
};
